import React, { Fragment, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Dialog } from 'components/Dialog/Dialog';
import { Button, ButtonVariant } from 'components/Button/Button';
import * as S from '../../../pages/Game/Game.style';
import { usePlayers } from 'context/PlayerContext';
import { avatars } from 'utils/avatars';
import plusSimple from '../../../assets/plusSimple.svg';
import { createDefaultScores } from '../../../utils/game';
import { GameType, useGame } from '../../../context/GameContext';
import { SettingsContainer } from '../../Dialog/Dialog.style';

interface GameSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

export const GameSettings: React.FC<GameSettingsProps> = ({
  isOpen,
  onClose,
}) => {
  const { gameSettings, updateSettings, currentRound } = useGame();
  const {
    players,
    handleAdditionalPlayer,
    togglePlayerActive,
    editPlayerName,
  } = usePlayers();
  const [tempActiveStatus, setTempActiveStatus] = useState<boolean[]>(
    players.map((p) => p.isActive)
  );
  const [editing, setEditing] = useState<Record<number, string | null>>({});

  const { handleSubmit, register, reset, watch } = useForm<{
    playerName: string;
  }>({
    defaultValues: { playerName: '' },
  });

  const playerName = watch('playerName', '');

  const addNewPlayer: SubmitHandler<{ playerName: string }> = ({
    playerName,
  }) => {
    if (playerName) {
      handleAdditionalPlayer({
        player: playerName,
        scores: createDefaultScores(gameSettings.totalRounds, currentRound),
        totalPoints: 0,
        isActive: true,
      });

      reset();
    }
  };

  const handleCheckboxChange = (index: number) => {
    const updatedStatus = [...tempActiveStatus];

    updatedStatus[index] = !updatedStatus[index];
    setTempActiveStatus(updatedStatus);
    togglePlayerActive(index);
  };

  const handleEditPlayer = (index: number, currentValue: string) => {
    editPlayerName(index, currentValue.trim());
    setEditing((prev) => {
      const { [index]: _, ...rest } = prev;

      return rest;
    });
  };

  const handleEditingStatus = (index: number, player: string) => {
    setEditing((prev) => ({
      ...prev,
      [index]: player,
    }));
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Game Settings">
      {gameSettings.type === GameType.SUM_OF_POINTS && (
        <SettingsContainer>
          <S.FormItem>
            <S.PlayerItem>
              <h4>Double points in last round</h4>
              <S.SwitcherContainer>
                <S.HiddenRadio
                  id="double-points-switch"
                  checked={gameSettings.doublePointsStatus}
                  onChange={() =>
                    updateSettings({
                      ...gameSettings,
                      doublePointsStatus: !gameSettings.doublePointsStatus,
                    })
                  }
                />
                <S.Switcher
                  htmlFor="double-points-switch"
                  isActive={gameSettings.doublePointsStatus}
                />
              </S.SwitcherContainer>
            </S.PlayerItem>
          </S.FormItem>
        </SettingsContainer>
      )}
      <SettingsContainer>
        <S.PlayerItem>
          <h4>Audio</h4>
          <S.SwitcherContainer>
            <S.HiddenRadio
              id="audio-switch"
              checked={gameSettings.audioStatus}
              onChange={() =>
                updateSettings({
                  ...gameSettings,
                  audioStatus: !gameSettings.audioStatus,
                })
              }
            />
            <S.Switcher
              htmlFor="audio-switch"
              isActive={gameSettings.audioStatus}
            />
          </S.SwitcherContainer>
        </S.PlayerItem>
      </SettingsContainer>
      <SettingsContainer>
        <form onSubmit={handleSubmit(addNewPlayer)}>
          <h4>Add new player</h4>
          <S.FlexItem>
            <S.FormItem>
              <label>Name</label>
              <input
                {...register('playerName', { required: true })}
                placeholder="Player"
              />
            </S.FormItem>
            <Button
              type="submit"
              variant={ButtonVariant.SUBMIT}
              disabled={!playerName.trim()}
            >
              <img src={plusSimple} alt="plus" />
            </Button>
          </S.FlexItem>
        </form>
      </SettingsContainer>
      <SettingsContainer>
        <h4>Active Players</h4>
        {players.map((item, index) => {
          const isLastPlayer = index === players.length - 1;
          const isEditing = editing[index] !== undefined;
          const currentValue = editing[index] ?? item.player;

          return (
            <Fragment key={index}>
              <S.FormItem>
                <S.PlayerItem>
                  <span>
                    <img
                      src={avatars[index]}
                      alt={`avatar-${index}`}
                      style={{ width: 32, height: 32, borderRadius: '50%' }}
                    />
                    {isEditing ? (
                      <>
                        <input
                          value={currentValue}
                          onChange={(e) =>
                            setEditing((prev) => ({
                              ...prev,
                              [index]: e.target.value,
                            }))
                          }
                        />
                        <Button
                          onClick={() => handleEditPlayer(index, currentValue)}
                          disabled={!currentValue.trim()}
                        >
                          ✔️
                        </Button>
                      </>
                    ) : (
                      <>
                        {item.player}
                        <Button
                          onClick={() =>
                            handleEditingStatus(index, item.player)
                          }
                        >
                          ✏️
                        </Button>
                      </>
                    )}
                  </span>

                  <S.SwitcherContainer>
                    <S.HiddenRadio
                      id={`switcher-${index}`}
                      checked={item.isActive}
                      onChange={() => handleCheckboxChange(index)}
                    />
                    <S.Switcher
                      htmlFor={`switcher-${index}`}
                      isActive={item.isActive}
                    />
                  </S.SwitcherContainer>
                </S.PlayerItem>
              </S.FormItem>
              {!isLastPlayer && <hr />}
            </Fragment>
          );
        })}
      </SettingsContainer>
    </Dialog>
  );
};
